import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { AuthModal } from '../components/auth';
import { Button } from '../components/ui';
import { CreatePost } from '../components/CreatePost';
import { Header } from '../components/Header';
import { Sidebar } from '../components/Sidebar';
import { RightSidebar } from '../components/RightSidebar';
import { MessagingNavigation } from '../components/MessagingNavigation';
import { EmptyState } from '../components/EmptyState';
import { UserFeed } from '../components/social/UserFeed';
import { PostSkeleton } from '../components/PostSkeleton';
import { PostCard } from '../components/PostCard';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { debugLogger } from '../utils/debugLogger';
import { postService, Post, Mood, Topic } from '../services/postService';
import { storageService } from '../services/storageService';
import { groupService } from '../services/groupService';
import { NotificationPopup } from '../components/NotificationPopup';
import { NotificationType } from '../services/notificationService';


const HomePage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [shouldShowPublicPosts, setShouldShowPublicPosts] = useState(false);
  
  const [publicPosts, setPublicPosts] = useState<Post[]>([]);
  const [publicPostsLoading, setPublicPostsLoadingState] = useState(false);
  const [publicPostsError, setPublicPostsError] = useState<string | null>(null);
  
  // Wrap setPublicPostsLoading with logging
  const setPublicPostsLoading = (value: boolean) => {
    debugLogger.log('HomePage', `⚡ setPublicPostsLoading: ${publicPostsLoading} → ${value}`, { 
      stackTrace: new Error().stack?.split('\n').slice(2, 4).join('\n')
    });
    setPublicPostsLoadingState(value);
  };

  // Log when publicPostsLoading state actually changes
  React.useEffect(() => {
    debugLogger.log('HomePage', `📊 publicPostsLoading state changed: ${publicPostsLoading}`, {
      postsCount: publicPosts.length,
      hasError: !!publicPostsError,
      isAuthenticated
    });
  }, [publicPostsLoading, publicPosts.length, publicPostsError, isAuthenticated]);
  const [publicPostsPage, setPublicPostsPage] = useState(0);
  const [hasMorePublicPosts, setHasMorePublicPosts] = useState(true);
  const [publicPostsRetryCount, setPublicPostsRetryCount] = useState(0);
  const [isRetryingPublicPosts, setIsRetryingPublicPosts] = useState(false);
  const [hasInitialLoadAttempted, setHasInitialLoadAttempted] = useState(false);

  const [suggestedGroups, setSuggestedGroups] = useState<any[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [selectedMood, setSelectedMood] = useState<Mood | 'all'>('all');
  const [selectedTopic, setSelectedTopic] = useState<Topic | string | 'all'>('all');
  const [notification, setNotification] = useState<any>(null); // For popup notifications
  const [addPostToFeed, setAddPostToFeed] = useState<((post: Post) => void) | null>(null); // Reference to UserFeed's addNewPost

  const handleOpenAuth = (mode: 'login' | 'register') => {
    debugLogger.logButtonClick('Auth Modal Open', { mode });
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  useEffect(() => {
    const handleAuthModalEvent = (event: CustomEvent) => {
      const { mode } = event.detail;
      handleOpenAuth(mode);
    };

    window.addEventListener('openAuthModal', handleAuthModalEvent as EventListener);
    
    return () => {
      window.removeEventListener('openAuthModal', handleAuthModalEvent as EventListener);
    };
  }, []);

  const loadPublicPosts = useCallback(async (page = 0, reset = false, isRetry = false) => {
    if (publicPostsLoading) {
      debugLogger.log('HomePage', 'Skipping load - already loading');
      return;
    }
    
    if (!reset && !isRetry && publicPostsError && hasInitialLoadAttempted) {
      debugLogger.log('HomePage', 'Skipping load - error state exists');
      return;
    }

    if (!hasMorePublicPosts && page > 0) {
      debugLogger.log('HomePage', 'Skipping load - no more posts');
      return;
    }

    setPublicPostsLoading(true);
    setHasInitialLoadAttempted(true);
    
    if (!isRetry) {
      setPublicPostsError(null);
    }

    try {
      debugLogger.log('HomePage', '📞 Calling getPublicPosts', { page, size: 10 });
      const response = await postService.getPublicPosts(page, 10);
      
      debugLogger.log('HomePage', '📦 Received response from getPublicPosts', { 
        hasResponse: !!response, 
        hasContent: !!response?.content,
        contentLength: response?.content?.length,
        responsePageNumber: response?.number,
        totalPages: response?.totalPages,
        totalElements: response?.totalElements,
        first: response?.first,
        last: response?.last
      });
      
      if (!response || !response.content || !Array.isArray(response.content)) {
        console.error('Invalid response structure:', response);
        throw new Error('Phản hồi không hợp lệ từ server');
      }
      
      debugLogger.log('HomePage', '✅ Public posts loaded successfully', {
        page,
        count: response.content.length,
        totalPages: response.totalPages || 0,
        totalElements: response.totalElements || 0,
        isRetry: isRetry ? `(retry ${publicPostsRetryCount + 1}/3)` : ''
      });

      if (reset || page === 0) {
        debugLogger.log('HomePage', '🔄 Setting posts (reset/initial)', { count: response.content.length });
        setPublicPosts(response.content);
      } else {
        debugLogger.log('HomePage', '🔄 Appending posts', { newCount: response.content.length });
        setPublicPosts(prev => {
          const existingIds = new Set(prev.map(post => post.id));
          const newPosts = response.content.filter(post => !existingIds.has(post.id));

          if (newPosts.length !== response.content.length) {
            console.warn(`Filtered out ${response.content.length - newPosts.length} duplicate posts on page ${page}`);
          }

          return [...prev, ...newPosts];
        });
      }

      setPublicPostsPage(page);
      
      const pageNumber = response.number ?? page;
      const totalPages = response.totalPages ?? 1;
      const hasMore = pageNumber < totalPages - 1;
      setHasMorePublicPosts(hasMore);

      debugLogger.log('HomePage', '🔄 Updated state', { 
        postsCount: response.content.length,
        currentPage: page,
        hasMore,
        pageNumber,
        totalPages,
        calculation: `${pageNumber} < ${totalPages - 1}`
      });

      setPublicPostsRetryCount(0);
      setIsRetryingPublicPosts(false);
      
      debugLogger.log('HomePage', '🎉 Public posts load complete - setting loading to false');
      
    } catch (error: any) {
      console.error('Failed to fetch public posts:', error);
      debugLogger.log('HomePage', 'Failed to fetch public posts', { error, retryCount: publicPostsRetryCount });

      const isNetworkError = error?.message?.includes('Network') || 
                            error?.message?.includes('fetch') ||
                            error?.code === 'ERR_NETWORK' ||
                            !error?.response;

      // Retry logic - only retry on network errors, max 2 retries
      if (isNetworkError && publicPostsRetryCount < 2 && !isRetry) {
        const newRetryCount = publicPostsRetryCount + 1;
        setPublicPostsRetryCount(newRetryCount);
        setIsRetryingPublicPosts(true);

        debugLogger.log('HomePage', `Retrying in 2 seconds... (${newRetryCount}/3)`);

        setTimeout(() => {
          loadPublicPosts(page, reset, true);
        }, 2000);
      } else {
        // Stop retrying and show error
        setIsRetryingPublicPosts(false);
        
        let errorMessage = 'Không thể kết nối đến server. ';
        
        if (isNetworkError) {
          errorMessage += 'Vui lòng kiểm tra kết nối internet và thử lại.';
        } else {
          errorMessage += 'Đã xảy ra lỗi. Vui lòng thử lại sau.';
        }
        
        setPublicPostsError(errorMessage);
        setPublicPostsRetryCount(0);
        
        debugLogger.log('HomePage', 'Stopped retrying - max attempts reached or non-network error');
      }
    } finally {
      debugLogger.log('HomePage', '🏁 Finally block - setting publicPostsLoading to FALSE');
      setPublicPostsLoading(false);
    }
  }, [publicPostsLoading, hasMorePublicPosts, publicPostsError, hasInitialLoadAttempted, publicPostsRetryCount]);

  const loadMorePublicPosts = useCallback(() => {
    debugLogger.log('HomePage', '🔄 loadMorePublicPosts called', {
      hasMore: hasMorePublicPosts,
      isLoading: publicPostsLoading,
      currentPage: publicPostsPage
    });
    
    if (hasMorePublicPosts && !publicPostsLoading) {
      debugLogger.log('HomePage', '✅ Loading next page', { nextPage: publicPostsPage + 1 });
      loadPublicPosts(publicPostsPage + 1, false);
    } else {
      debugLogger.log('HomePage', '⏸️ Not loading', {
        reason: !hasMorePublicPosts ? 'No more posts' : 'Already loading'
      });
    }
  }, [hasMorePublicPosts, publicPostsLoading, publicPostsPage, loadPublicPosts]);

  const handleRetryPublicPosts = () => {
    setPublicPostsError(null);
    setPublicPostsRetryCount(0);
    setPublicPostsPage(0);
    setPublicPosts([]);
    setHasMorePublicPosts(true);
    setHasInitialLoadAttempted(false);
    loadPublicPosts(0, true);
  };

  useEffect(() => {
    // Only load once when component mounts for non-authenticated users
    if (!isAuthenticated) {
      loadPublicPosts(0, true);
    } else {
      // Reset public posts state when user authenticates
      setShouldShowPublicPosts(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // Load when authentication status changes

  const handleCreatePost = async (
    content: string,
    visibility?: 'public_' | 'friends' | 'private_' | 'group',
    mediaFiles?: File[]
  ) => {
    debugLogger.logFormSubmit('Create Post', { content, visibility, mediaFilesCount: mediaFiles?.length || 0 });
    try {
      let mediaUrls: string[] | undefined = undefined
      if (mediaFiles && mediaFiles.length > 0) {
        mediaUrls = await storageService.uploadFiles(mediaFiles)
      }
      const post = await postService.createPost({
        content,
        visibility: (visibility === 'private_' ? 'private_' : (visibility as any)),
        mediaUrls
      });
      debugLogger.log('HomePage', 'Post created successfully', { postId: post?.id });
      
      // Add post to feed immediately
      if (post && addPostToFeed) {
        debugLogger.log('HomePage', 'Adding post to feed via callback');
        addPostToFeed(post);
      } else {
        debugLogger.log('HomePage', 'addPostToFeed not available', { hasPost: !!post, hasCallback: !!addPostToFeed });
      }

      // Show success notification
      setNotification({
        id: Date.now(),
        type: NotificationType.SYSTEM,
        title: 'Đăng bài thành công',
        message: 'Bài viết của bạn đã được đăng thành công!',
        timestamp: new Date().toISOString(),
        avatarUrl: user?.avatarUrl,
        userName: user?.displayName,
        userId: user?.id
      });
      
      // Return the post - media will be updated via WebSocket
      return post;
    } catch (error) {
      debugLogger.log('HomePage', 'Failed to create post', error);
      console.error('Failed to create post:', error);
      
      // Show error notification
      setNotification({
        id: Date.now(),
        type: NotificationType.SYSTEM,
        title: 'Lỗi đăng bài',
        message: 'Không thể đăng bài viết. Vui lòng thử lại.',
        timestamp: new Date().toISOString()
      });
      
      throw error; // Re-throw so CreatePost can show error
    }
  };

  useEffect(() => {
    const fetchSuggestedGroups = async () => {
      // Only fetch if authenticated
      if (!isAuthenticated) {
        setSuggestedGroups([]);
        return;
      }

      setLoadingGroups(true);
      setGroupsError(null);
      
      try {
        // Use AI-powered recommendations endpoint
        const response = await groupService.getGroupRecommendations(0, 10);
        
        if (response && Array.isArray(response.content)) {
          setSuggestedGroups(response.content);
        } else if (Array.isArray(response)) {
          setSuggestedGroups(response);
        } else {
          console.warn('Group recommendations response is not in expected format:', response);
          setSuggestedGroups([]);
        }
        
        debugLogger.log('HomePage', 'Group recommendations loaded', { count: response?.content?.length || 0 });
      } catch (error: any) {
        console.error('Failed to fetch group recommendations:', error);
        
        const isNetworkError = error?.message?.includes('Network') || 
                              error?.message?.includes('fetch') ||
                              error?.code === 'ERR_NETWORK' ||
                              !error?.response;
        
        const errorMessage = isNetworkError 
          ? 'Không thể kết nối đến server để tải nhóm gợi ý.'
          : 'Không thể tải danh sách nhóm. Vui lòng thử lại sau.';
        
        setGroupsError(errorMessage);
        setSuggestedGroups([]);
        
        debugLogger.log('HomePage', 'Failed to load group recommendations', { error: error?.message });
      } finally {
        setLoadingGroups(false);
      }
    };

    fetchSuggestedGroups();
  }, [isAuthenticated]);

  // Infinite scroll for public posts (only for unauthenticated users)
  useInfiniteScroll({
    hasMore: !isAuthenticated ? hasMorePublicPosts : false,
    isLoading: !isAuthenticated ? publicPostsLoading : false,
    onLoadMore: !isAuthenticated ? loadMorePublicPosts : () => {},
    threshold: 200
  });

  // Notifications are now handled in Header component, no need for duplicate logic here

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200">
        <Header showSearch={true} showUserMenu={true} />
        <main className="w-full py-4 px-1 sm:px-2 lg:px-4 xl:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-4 xl:gap-6 max-w-none">
            <div className="hidden lg:block lg:col-span-2">
              <Sidebar />
            </div>
            
            <div className="lg:col-span-7">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6 max-w-2xl mx-auto">
                <div className="p-4 text-center py-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Chia sẻ suy nghĩ của bạn</h3>
                  <p className="text-gray-500 mb-4">Đăng nhập để tạo bài viết và tương tác với cộng đồng</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      onClick={() => handleOpenAuth('login')}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Đăng nhập
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleOpenAuth('register')}
                      className="border-gray-300 text-gray-700 hover:border-blue-500 hover:text-blue-600"
                    >
                      Đăng ký
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                {publicPostsError ? (
                  <div className="flex flex-col items-center justify-center p-6 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Lỗi tải bài viết</h3>
                    <p className="text-gray-500 mb-4">{publicPostsError}</p>
                    <Button
                      onClick={() => {
                        setPublicPostsError(null);
                        setPublicPostsRetryCount(0);
                        setPublicPostsPage(0);
                        setPublicPosts([]);
                        setHasMorePublicPosts(true);
                        setIsRetryingPublicPosts(false);
                        setHasInitialLoadAttempted(false);
                        loadPublicPosts(0, true);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Thử lại
                    </Button>
                  </div>
                ) : publicPostsLoading && (!publicPosts || publicPosts.length === 0) ? (
                  <>
                    <PostSkeleton />
                    <PostSkeleton />
                    <PostSkeleton />
                  </>
                ) : (!publicPosts || publicPosts.length === 0) ? (
                  <EmptyState
                    type="empty"
                    title="Chưa có bài viết công khai"
                    description="Hãy đăng ký để bắt đầu chia sẻ và khám phá nội dung từ cộng đồng"
                    buttonText="Tham gia ngay"
                    onButtonClick={() => handleOpenAuth('register')}
                  />
                ) : (
                  <>
                    {publicPosts.map((post) => (
                      <PostCard
                        key={post.id}
                        post={post}
                        currentUserId={undefined}
                        onReaction={(_id, _reaction) => {}}
                        onUnreact={(_id) => {}}
                        onComment={async (_id, _content) => {}}
                        onShare={(_id) => {}}
                        onDelete={() => {}}
                      />
                    ))}
                    
                    {/* Loading more posts */}
                    {publicPostsLoading && publicPosts.length > 0 && (
                      <>
                        {isRetryingPublicPosts && publicPostsRetryCount > 0 ? (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center justify-center space-x-3">
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-yellow-600 border-t-transparent"></div>
                              <div className="text-sm text-yellow-800">
                                <p className="font-medium">Kết nối thất bại. Đang thử lại...</p>
                                <p className="text-xs mt-0.5">Lần thử {publicPostsRetryCount}/3</p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <PostSkeleton />
                            <PostSkeleton />
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
            
            <div className="hidden lg:block lg:col-span-3">
              <div className="space-y-4">
                <RightSidebar suggestedGroups={suggestedGroups} />
              </div>
            </div>
          </div>
        </main>

        <AuthModal
          isOpen={showAuthModal}
          initialMode={authMode}
          onClose={() => setShowAuthModal(false)}
        />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200">
        <Header showSearch={true} showUserMenu={true} />
        <main className="w-full py-4 px-1 sm:px-2 lg:px-4 xl:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-4 xl:gap-6 max-w-none">
            <div className="hidden lg:block lg:col-span-2">
              <Sidebar />
            </div>
            
            <div className="lg:col-span-7">
            <div className="max-w-2xl mx-auto w-full">
            <CreatePost onCreatePost={handleCreatePost} />
            
            {/* Modern Feed Filters */}
            {isAuthenticated && !shouldShowPublicPosts && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4 overflow-hidden">
                {/* Filters Section */}
                <div className="p-4 space-y-4">
                  {/* Mood Filter */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm font-semibold text-gray-700">Cảm xúc</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button 
                        onClick={() => setSelectedMood('all')} 
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          selectedMood === 'all' 
                            ? 'bg-blue-600 text-white shadow-sm' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Tất cả
                      </button>
                      <button onClick={() => setSelectedMood('Enjoyment')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedMood === 'Enjoyment' ? 'bg-yellow-100 text-yellow-800 ring-2 ring-yellow-500' : 'bg-gray-100 text-gray-700 hover:bg-yellow-50'}`}>
                        😊 Vui vẻ
                      </button>
                      <button onClick={() => setSelectedMood('Sadness')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedMood === 'Sadness' ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-500' : 'bg-gray-100 text-gray-700 hover:bg-blue-50'}`}>
                        😢 Buồn
                      </button>
                      <button onClick={() => setSelectedMood('Anger')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedMood === 'Anger' ? 'bg-red-100 text-red-800 ring-2 ring-red-500' : 'bg-gray-100 text-gray-700 hover:bg-red-50'}`}>
                        😠 Giận
                      </button>
                      <button onClick={() => setSelectedMood('Fear')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedMood === 'Fear' ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-500' : 'bg-gray-100 text-gray-700 hover:bg-blue-50'}`}>
                        😨 Sợ
                      </button>
                      <button onClick={() => setSelectedMood('Surprise')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedMood === 'Surprise' ? 'bg-orange-100 text-orange-800 ring-2 ring-orange-500' : 'bg-gray-100 text-gray-700 hover:bg-orange-50'}`}>
                        😲 Ngạc nhiên
                      </button>
                      <button onClick={() => setSelectedMood('Disgust')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedMood === 'Disgust' ? 'bg-green-100 text-green-800 ring-2 ring-green-500' : 'bg-gray-100 text-gray-700 hover:bg-green-50'}`}>
                        🤢 Ghê
                      </button>
                      <button onClick={() => setSelectedMood('Other')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedMood === 'Other' ? 'bg-gray-200 text-gray-800 ring-2 ring-gray-500' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                        😐 Khác
                      </button>
                    </div>
                  </div>

                  {/* Topic Filter */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-4 h-4 text-cyan-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-semibold text-gray-700">Chủ đề</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button 
                        onClick={() => setSelectedTopic('all')} 
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          selectedTopic === 'all' 
                            ? 'bg-slate-600 text-white shadow-sm' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Tất cả
                      </button>
                      <button onClick={() => setSelectedTopic(Topic.THE_THAO)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedTopic === Topic.THE_THAO ? 'bg-orange-100 text-orange-800 ring-2 ring-orange-500' : 'bg-gray-100 text-gray-700 hover:bg-orange-50'}`}>
                        ⚽ Thể thao
                      </button>
                      <button onClick={() => setSelectedTopic(Topic.VI_TINH)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedTopic === Topic.VI_TINH ? 'bg-cyan-100 text-cyan-800 ring-2 ring-cyan-500' : 'bg-gray-100 text-gray-700 hover:bg-cyan-50'}`}>
                        💻 Vi tính
                      </button>
                      <button onClick={() => setSelectedTopic(Topic.SUC_KHOE)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedTopic === Topic.SUC_KHOE ? 'bg-teal-100 text-teal-800 ring-2 ring-teal-500' : 'bg-gray-100 text-gray-700 hover:bg-teal-50'}`}>
                        ❤️ Sức khỏe
                      </button>
                      <button onClick={() => setSelectedTopic(Topic.DOI_SONG)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedTopic === Topic.DOI_SONG ? 'bg-green-100 text-green-800 ring-2 ring-green-500' : 'bg-gray-100 text-gray-700 hover:bg-green-50'}`}>
                        🏠 Đời sống
                      </button>
                      <button onClick={() => setSelectedTopic(Topic.KINH_DOANH)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedTopic === Topic.KINH_DOANH ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-500' : 'bg-gray-100 text-gray-700 hover:bg-blue-50'}`}>
                        💼 Kinh doanh
                      </button>
                      <button onClick={() => setSelectedTopic(Topic.KHOA_HOC)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedTopic === Topic.KHOA_HOC ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-500' : 'bg-gray-100 text-gray-700 hover:bg-blue-50'}`}>
                        🔬 Khoa học
                      </button>
                      <button onClick={() => setSelectedTopic(Topic.VAN_HOA)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedTopic === Topic.VAN_HOA ? 'bg-pink-100 text-pink-800 ring-2 ring-pink-500' : 'bg-gray-100 text-gray-700 hover:bg-pink-50'}`}>
                        🎭 Văn hóa
                      </button>
                      <button onClick={() => setSelectedTopic(Topic.THE_GIOI)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedTopic === Topic.THE_GIOI ? 'bg-indigo-100 text-indigo-800 ring-2 ring-indigo-500' : 'bg-gray-100 text-gray-700 hover:bg-indigo-50'}`}>
                        🌍 Thế giới
                      </button>
                      <button onClick={() => setSelectedTopic(Topic.PHAP_LUAT)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedTopic === Topic.PHAP_LUAT ? 'bg-gray-200 text-gray-800 ring-2 ring-gray-500' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                        ⚖️ Pháp luật
                      </button>
                      <button onClick={() => setSelectedTopic(Topic.CHINH_TRI_XA_HOI)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedTopic === Topic.CHINH_TRI_XA_HOI ? 'bg-red-100 text-red-800 ring-2 ring-red-500' : 'bg-gray-100 text-gray-700 hover:bg-red-50'}`}>
                        🏛️ Chính trị
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            </div>
            
            {/* Only show UserFeed if token validation hasn't failed */}
            {!shouldShowPublicPosts && (
              <UserFeed
                className="space-y-4"
                moodFilter={selectedMood}
                topicFilter={selectedTopic}
                onNewPost={(callback) => {
                  debugLogger.log('HomePage', 'Received addNewPost callback from UserFeed');
                  setAddPostToFeed(() => callback);
                }}
                onDeletePost={(postId) => {
                  // This will be handled internally by UserFeed for authenticated users
                  console.log('Post deleted:', postId);
                }}
                onTokenValidationFailed={() => {
                  debugLogger.log('HomePage', 'Token validation failed, switching to public posts');
                  setShouldShowPublicPosts(true);
                  loadPublicPosts(0, true);
                }}
              />
            )}
            
            {/* Show public posts if token validation failed */}
            {shouldShowPublicPosts && (
              <div className="space-y-4 mt-4">
                {publicPostsError && publicPostsRetryCount < 3 && (
                  <div className="mb-4 rounded-lg bg-yellow-50 border border-yellow-200 p-3">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-yellow-600 border-t-transparent"></div>
                      <div className="text-sm text-yellow-800">
                        <p className="font-medium">Kết nối thất bại. Đang thử lại...</p>
                        <p className="text-xs mt-0.5">Lần thử {publicPostsRetryCount}/3</p>
                      </div>
                    </div>
                  </div>
                )}

                {publicPostsError && publicPostsRetryCount >= 3 && (
                  <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3">
                    <p className="text-sm text-red-800">
                      Không thể tải bài viết. Vui lòng thử lại sau.
                    </p>
                  </div>
                )}

                {publicPosts.length === 0 && publicPostsLoading && (
                  <>
                    <PostSkeleton />
                    <PostSkeleton />
                  </>
                )}

                {publicPosts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    currentUserId={undefined}
                    onReaction={() => {}}
                    onUnreact={() => {}}
                    onComment={async () => {}}
                    onShare={() => {}}
                    onDelete={() => {}}
                  />
                ))}

                {publicPostsLoading && publicPosts.length > 0 && (
                  <PostSkeleton />
                )}
              </div>
            )}

          </div>
          
          <div className="hidden lg:block lg:col-span-3">
            <div className="space-y-4">
              <RightSidebar suggestedGroups={suggestedGroups} />
              {isAuthenticated && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Quick Messages</h3>
                  <MessagingNavigation 
                    showSearch={true}
                    showCreateButton={true}
                    maxConversations={3}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Notification Popup */}
      {notification && (
        <NotificationPopup
          notification={notification}
          onClose={() => setNotification(null)}
          autoCloseDelay={5000}
        />
      )}
      </div>
    </>
  );
};

export default HomePage;